import AutoLogout from '@/components/common/AutoLogout';
import StoreRehydrationSync from '@/components/common/StoreRehydrationSync';
import '@/styles/globals.css';
// Design system CSS - scoped to .landing-page-v2-container so it won't affect other pages
import '@/design-system/foundations/index.css';
import '@/components/landing-page-v2/styles/mobile.css';
import { appWithTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { Toaster } from 'sonner';

const DEFAULT_META_TITLE =
  'Nexgo AI – Travel Discovery Super App for Authentic Journeys';
// import GoogleAnalytics from '@/components/common/GoogleAnalytics';
import CurrencyInitializer from '@/components/CurrencyInitializer';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { forceNavigate } from '@/utils/safeRouterPush';
// Import image cache debug utilities (makes them available in console)
import '@/utils/imageCacheDebug';

// Dynamically import devtools only in development (client-side only)
const ReactQueryDevtools = dynamic(
  () =>
    process.env.NODE_ENV === 'development'
      ? import('@tanstack/react-query-devtools').then(res => ({
          default: res.ReactQueryDevtools,
        }))
      : Promise.resolve({ default: () => null }),
  { ssr: false }
);

function cleanupDocumentBodyLocks() {
  if (typeof document === 'undefined') return;
  document.body.classList.remove('overflow-hidden');
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  document.body.style.pointerEvents = '';
  document.body.removeAttribute('data-scroll-locked');
  document.body.style.removeProperty('padding-right');
}

function App({ Component, pageProps }: AppProps) {
  // Create QueryClient instance per app instance to avoid sharing state between users in SSR
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      })
  );

  // Use client-side mounted state to prevent hydration mismatch
  const [isMounted, setIsMounted] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Clear leftover scroll/pointer locks from modals when routes change so the
  // next page is always interactive (Radix Select/Dialog, video overlays, etc.)
  useEffect(() => {
    let hungNavTimer: number | undefined;

    const onStart = (url: string) => {
      cleanupDocumentBodyLocks();
      window.clearTimeout(hungNavTimer);
      // Only watch transitions that leave a journey/package detail page.
      // Those are the ones that can update the URL without unmounting the UI.
      if (!router.pathname.includes('package-details')) return;
      hungNavTimer = window.setTimeout(() => {
        forceNavigate(url);
      }, 2500);
    };
    const onDone = () => {
      cleanupDocumentBodyLocks();
      window.clearTimeout(hungNavTimer);
    };

    router.events.on('routeChangeStart', onStart);
    router.events.on('routeChangeComplete', onDone);
    router.events.on('routeChangeError', onDone);
    return () => {
      window.clearTimeout(hungNavTimer);
      router.events.off('routeChangeStart', onStart);
      router.events.off('routeChangeComplete', onDone);
      router.events.off('routeChangeError', onDone);
    };
  }, [router.events, router.pathname]);

  const gtmScript = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-TNWMCF3Q');`;

  const metaPixelScript = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '2041287699835136');
fbq('track', 'PageView');`;

  return (
    <QueryClientProvider client={queryClient}>
      <Head>
        <title>{DEFAULT_META_TITLE}</title>
      </Head>
      <Script id='gtm-inline' strategy='afterInteractive'>
        {gtmScript}
      </Script>
      <Script id='meta-pixel' strategy='afterInteractive'>
        {metaPixelScript}
      </Script>
      {/* Sync persisted auth/user stores and profile from localStorage after login redirect */}
      <StoreRehydrationSync />
      {/* Google Maps script removed - loaded by useJsApiLoader in map components */}
      <AutoLogout />
      <CurrencyInitializer />
      <Component {...pageProps} />
      <Toaster position='top-center' richColors />
      {isMounted && process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}

export default appWithTranslation(App);
